/**
 * The number the header's seven-segment readout displays.
 *
 * Kept out of the animation entry file for the same reason as the serialiser: it
 * is pure, so it can be unit tested without a DOM, and an entry file that exports
 * anything bundles as an ES module when it is loaded as a classic script.
 */

/**
 * Map the sum of squared normalised particle weights to a displayed reading.
 *
 * The chi-squared divergence of the normalised weights from uniform is
 *
 *   D = N * sum_i w_i^2 - 1 = N/ESS - 1,
 *
 * which runs from 0 for a uniform cloud to N-1 when one particle carries every
 * unit of weight. Squashing it through 2D/(1+D) bounds the display and, more
 * usefully, puts the reading at exactly 1 when ESS = N/2 — the conventional
 * threshold for calling a particle filter degenerate. So the leading digit says
 * which side of that threshold the filter is on, and nothing else has to.
 *
 * Returns a value in [0, 2N/(N+1)): 1.954545… at N = 44.
 */
export function divergenceReading(sumSq: number, n: number): number {
  // Floating-point error on a uniform cloud can put N*sumSq a hair under 1 and
  // make D negative, which would render as a minus sign the display cannot draw.
  const d = n * sumSq - 1
  return d > 0 ? (2 * d) / (1 + d) : 0
}

/**
 * Smoothing factor for the running average, as an exponential moving average.
 *
 * The per-step value is unusable raw. This filter resamples every step, so
 * consecutive weight vectors are close to independent and the per-step reading
 * behaves like noise: measured over 40000 steps it has median 0.66 and sits above 1
 * on 33% of steps, crossing that line every 0.7 seconds of animation. Both the
 * digits and any threshold on them are meaningless at that rate.
 *
 * 0.06 is a half-life of 2.8 seconds at 4 steps/s.
 *
 * IMPORTANT: smooth `sumSq` and then map it, never the other way round. Averaging
 * the mapped reading destroys the threshold identity, because the map is nonlinear,
 * and it also buries the answer: measured that way the display sat above 1 on only
 * 3.7% of steps, in 3.4-second excursions once every 101 seconds, so a reader
 * essentially never saw the indicator do anything. Averaging sumSq is both the
 * correct way to aggregate ESS over time and far more legible: above 1 for 76% of
 * the time, crossing every 22 seconds, dwelling 17 seconds above and 5 below.
 *
 * The level the average settles at is a property of the filter, not the display. It
 * is set mostly by particle count: at 44 particles the reading sat above 1 on 80% of
 * steps, and raising it to 120 centred it on the threshold.
 */
export const READING_SMOOTHING = 0.06

/**
 * Hysteresis band for the worldline indicator, bracketing 1.
 *
 * The filter is tuned so the reading straddles 1 — measured median 0.999, above the
 * line on 50% of steps — which is what makes the indicator worth watching. The cost
 * of sitting on the threshold is that a bare crossing test fires every 5 seconds.
 * +/-0.08 takes that to every 12.4 seconds, dwelling 13.3s above and 11.4s below, so
 * the split stays even while the flicker stays an event.
 */
export const BETA_ENTER = 1.08
export const BETA_EXIT = 0.92

/**
 * Blend a new observation into a running average.
 *
 * `count` is how many observations have already been folded in. The first few use a
 * plain running mean, because a fixed 0.06 would take seconds to walk away from
 * whatever the first value happened to be — and the first value comes from a cloud
 * deliberately scattered off the attractor, so it is not typical.
 */
export function smoothed(previous: number, next: number, count: number): number {
  const alpha = Math.max(READING_SMOOTHING, 1 / (count + 1))
  return previous + alpha * (next - previous)
}

/**
 * Clamp a restored sum of squared weights to the range it can actually take.
 *
 * A snapshot written by an older build holds a different quantity in this slot and
 * is the same byte length, so nothing else rejects it. Clamping makes a stale value
 * harmless rather than letting it drive the display somewhere impossible.
 */
export function clampSumSq(sumSq: number, n: number): number {
  if (!Number.isFinite(sumSq)) return 1 / n
  return Math.min(1, Math.max(1 / n, sumSq))
}
